import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { openRegistrationSchema } from '@shared/lib/validators/registration-schema';
import { InputField } from '@shared/ui/input-field';
import { Button } from '@shared/ui/button';
import AuthLayout from '@shared/ui/access-layout';
import ReactInputMask from 'react-input-mask';
import { useAppDispatch } from '../../../app/store/store';
import {
  setErrorNotification,
  setNotification,
} from '../../../app/store/notifications';
import { authApi } from '../../../api/services/auth.service';

interface RegistrationFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name?: string;
  patronymic?: string;
  mobile_phone?: string;
  city?: string;
  company?: string;
}

const OpenRegistration = () => {
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegistrationFormData>({
    mode: 'onChange',
    resolver: yupResolver(openRegistrationSchema),
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    document.title = 'Регистрация';
  }, []);

  // Тот же баг, что и в invite-регистрации (RegistrationStepCredentials):
  // yup-схема не знает «занят/не занят», эту проверку делает только бэк.
  // Если опираться только на тост из catch'а — поле логина не краснеет
  // и юзер ничего не понимает; если ставить setError на onBlur, при
  // нажатии «Зарегистрироваться» yup-валидация затирает ручную ошибку.
  // Решение зеркальное: предварительная проверка занятости логина прямо
  // в onSubmit — там setError становится «последним словом».
  // Принцип: единственный «громкий» тост — от бэка (см. catch'и
  // register/я), всё локальное — только в подсветку поля. Раньше
  // здесь дополнительно крутилось `setErrorNotification('Логин
  // занят!')`, и при сабмите юзер видел два одинаковых тоста подряд
  // (один из onBlur, второй — из pre-check). Сейчас оставлен только
  // setError, чтобы поле краснело без дублирующего уведомления.
  const handleUsernameBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const username = event.target.value.trim();
    if (!username) return;
    try {
      const response = await authApi.checkUsernameAvailable(username);
      if (response.data) {
        // Лёгкий «положительный» тост оставляем, у него нет дубликата.
        dispatch(setNotification('Логин не занят!'));
      } else {
        setError('username', {
          type: 'manual',
          message: 'Логин занят!',
        });
      }
    } catch {
      // Тихо: даже если check сорвался, onSubmit всё равно проверит.
    }
  };

  const onSubmit = async (data: RegistrationFormData) => {
    // Перед сабмитом точечно проверяем занятость логина.
    // setError здесь срабатывает ПОСЛЕ yup и держит подсветку до тех
    // пор, пока юзер не изменит логин. Тостами тут не сорим — если
    // дойдёт до register и бэк всё-таки откажет, его сообщение покажет
    // catch ниже.
    const username = (data.username || '').trim();
    if (username) {
      try {
        const response = await authApi.checkUsernameAvailable(username);
        if (!response.data) {
          setError(
            'username',
            {
              type: 'manual',
              message: 'Логин занят!',
            },
            { shouldFocus: true }
          );
          return;
        }
      } catch {
        dispatch(setErrorNotification('Не удалось проверить логин.'));
        return;
      }
    }

    try {
   const trim = (v?: string) => (typeof v === 'string' ? v.trim() : '');
      const lastName = trim(data.last_name);
      const patronymic = trim(data.patronymic);
      const mobilePhone = trim(data.mobile_phone);

      const registrationData: {
        username: string;
        email: string;
        password: string;
        first_name: string;
        last_name?: string;
        patronymic?: string;
        mobile_phone?: string;
      } = {
        username: data.username,
        email: data.email,
        password: data.password,
        first_name: data.first_name,
      };
      if (lastName) registrationData.last_name = lastName;
      if (patronymic) registrationData.patronymic = patronymic;
      if (mobilePhone) registrationData.mobile_phone = mobilePhone;

      await authApi.register(registrationData);

      dispatch(
        setNotification(
          'Регистрация успешна! Пожалуйста, проверьте вашу почту для подтверждения.'
        )
      );
      setIsRegistered(true);
    } catch (error: any) {
      // На случай race-condition'а: между нашей проверкой занятости и
      // самим register'ом кто-то занял этот логин. Бэк вернёт 400 с
      // username: ['...']. Пробрасываем это в подсветку поля, а не
      // только в тост — иначе юзер опять не поймёт, что чинить.
      const usernameErr = error?.response?.data?.username?.[0];
      const emailErr = error?.response?.data?.email?.[0];
      if (usernameErr) {
        setError(
          'username',
          { type: 'server', message: usernameErr },
          { shouldFocus: true }
        );
      }
      if (emailErr) {
        setError('email', { type: 'server', message: emailErr });
      }
      const errorMessage =
        error?.response?.data?.detail ||
        usernameErr ||
        emailErr ||
        'Произошла ошибка при регистрации. Попробуйте позже.';

      dispatch(setErrorNotification(errorMessage));
    }
  };

  if (isRegistered) {
    return (
      <AuthLayout
        leftContent={
          <section className="flex justify-center items-center flex-col absolute top-[45%] left-[25%] -translate-x-1/2 -translate-y-[20%] text-center w-[346px]">
            <h2 className="text-[28px] text-white">Регистрация завершена!</h2>
            <div className="my-[34px] mx-auto w-[113px] border border-white"></div>
            <div className="font-medium text-[22px] mt-[34px] leading-[140%] text-white">
              Проверьте вашу почту для подтверждения регистрации
            </div>
          </section>
        }
      >
        <div className="w-[70%] relative">
          <h2 className="font-bold text-[28px] text-[#141414]">Подтверждение почты</h2>
          <div className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]" style={{ marginBottom: '20px' }}>
            Мы отправили письмо с подтверждением на указанный вами email адрес.
            Пожалуйста, проверьте вашу почту и следуйте инструкциям для
            подтверждения регистрации.
          </div>
          <div className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]" style={{ marginBottom: '20px' }}>
            После подтверждения вы сможете войти в личный кабинет.
          </div>
          <Button onClick={() => navigate('/login')}>
            Перейти на страницу входа
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      leftContent={
        <section className="flex justify-center items-center flex-col absolute top-[45%] left-[25%] -translate-x-1/2 -translate-y-[20%] text-center w-[346px]">
          <h2 className="text-[28px] text-white">Регистрация</h2>
          <div className="my-[34px] mx-auto w-[113px] border border-white"></div>
          <div className="font-medium text-[22px] mt-[34px] leading-[140%] text-white">
            Заполните форму, чтобы создать личный кабинет
          </div>
        </section>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="w-[70%] relative">
        <h2 className="font-bold text-[28px] text-[#141414]">Создать аккаунт</h2>
        <div className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]">
          Пожалуйста, заполните обязательные поля для регистрации
        </div>

        <div className="mt-[30px]">
          <InputField name="first_name" inputName="first_name" id="first_name" label="Имя *" placeholder="Введите ваше имя" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <InputField name="last_name" inputName="last_name" id="last_name" label="Фамилия" placeholder="Введите вашу фамилию" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <InputField name="patronymic" inputName="patronymic" id="patronymic" label="Отчество" placeholder="Введите ваше отчество" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <InputField name="email" inputName="email" id="email" type="email" label="Email *" placeholder="Введите ваш email" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <InputField
            name="username"
            inputName="username"
            id="username"
            label="Логин *"
            placeholder="Введите логин"
            register={register}
            errors={errors}
            onBlur={handleUsernameBlur}
          />
        </div>
        <div className="mt-[30px]">
          <InputField name="password" inputName="password" type="password" id="password" label="Пароль *" placeholder="Введите пароль" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <InputField name="confirmPassword" inputName="confirmPassword" type="password" id="confirmPassword" label="Подтвердите пароль *" placeholder="Повторите пароль" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <Controller
            name="mobile_phone"
            control={control}
            render={({ field }) => (
              <InputField
                {...field}
                as={ReactInputMask}
                mask="+7 (***) ***-**-**"
                id="mobile_phone"
                name="mobile_phone"
                label="Телефон"
                placeholder="+7 (___) ___-__-__"
                errors={errors}
              />
            )}
          />
        </div>
        <div className="mt-[30px]">
          <InputField name="city" inputName="city" id="city" label="Город" placeholder="Введите ваш город" register={register} errors={errors} />
        </div>
        <div className="mt-[30px]">
          <InputField name="company" inputName="company" id="company" label="Компания" placeholder="Введите название компании" register={register} errors={errors} />
        </div>

        <div className="flex justify-between gap-2 mt-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Регистрация...' : 'Зарегистрироваться'}
          </Button>
        </div>

        <div className="mt-[45px] text-[#b4b4b4] text-base leading-[140%]" style={{ marginTop: '20px' }}>
          Уже зарегистрированы?{' '}
          <Link to="/login" style={{ cursor: 'pointer', color: 'blue' }}>
            Войти
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default OpenRegistration;
